import { MaterialGroup, PartItem } from '@cutr/constants/cutlist';
import { BackendCutlistGroup } from '@cutr/constants/cutlist-backend';
import { NestingOutput } from '@cutr/nesting';

import { getMaterial } from '@/api/materials';
import {
  getEdgeTrimsFromMaterialGroup,
  getMaterialsFromMaterialGroup,
  getPricingMaterialsFromPart,
} from '@/api/materials';
import { useMaterialGroupState } from '@/api/materialsGroup';
import { SheetGroup } from '@/api/nesting';
import {
  isValidBeamPart,
  isValidPart,
  partsSelector,
  useCutlistState,
} from '@/api/store';

import { isTruthy } from './misc';

/**
 * Transform a frontend PartItem to backend part format
 */
export const transformPartToBackend = (part: PartItem, index: number) => ({
  index: index,
  shortId: part.shortId,
  groupId: part.groupId,
  label: part.label,
  quantity: part.quantity,
  grainDirection: part.grainDirection,
  createLabel: part.createLabel,
  widthMM: part.widthMM,
  lengthMM: part.lengthMM,
  thicknessUM: part.thickness,
  partType: part.partType,
  cncSeconds: part.cncSeconds,
  grooves: part.grooves,
  length1RoundedEdgeband: part.roundedEdgeband?.length1,
  length2RoundedEdgeband: part.roundedEdgeband?.length2,
  width1RoundedEdgeband: part.roundedEdgeband?.width1,
  width2RoundedEdgeband: part.roundedEdgeband?.width2,
  aiSuggestions: part.aiSuggestions,
  aiMeta: part.aiMeta,
  ...getPricingMaterialsFromPart(part),
  beamMaterialId: part.beamMaterialId || null,
  beamRounded: part.beamRounded,
});

/**
 * Transform an array of frontend parts to backend format with sorting
 */
export const transformPartsToBackend = (parts: PartItem[]) => {
  return parts
    .sort(
      (a, b) =>
        ((a.createdAt || a.index) ?? 0) - ((b.createdAt || b.index) ?? 0)
    )
    .map(transformPartToBackend);
};

/**
 * Transform a material group to backend format
 */
export const transformMaterialGroupToBackend = (
  materialGroup: MaterialGroup,
  groupIndex: number,
  nestingOutput?: NestingOutput
) => {
  const core1 = getMaterial(materialGroup.core1);
  const automaticSheetSizeMaterialIds =
    materialGroup.automaticSheetSizeMaterials.map(
      (material) => getMaterial(material)?.id
    );

  return {
    nestingOutput,
    ulid: materialGroup.ulid,
    index: groupIndex + 1,
    groupId: materialGroup.id,
    name: core1?.name || materialGroup.name,
    edgeProfileType: materialGroup.edgeProfile,
    continuousGrain: materialGroup.continuousGrain,
    additionalProcessing: materialGroup.additionalProcessing,
    paintColor: materialGroup.paintColor,
    sheetStackingType: materialGroup.sheetStackingType,
    paintThicknessUM: materialGroup.paintThicknessUM,
    createLabels: materialGroup.createLabels,
    aiSuggestions: materialGroup.aiSuggestions,
    automaticSheetSizeMaterialIds,
    sheetSizeSelection: materialGroup.sheetSizeSelection,
    ...getMaterialsFromMaterialGroup(materialGroup),
    ...getEdgeTrimsFromMaterialGroup(materialGroup),
    beamMaterialId: materialGroup.beamMaterialId,
  };
};

/**
 * Create backend grouping from the frontend material groups.
 *
 * @param sheetGroups - contains information that updates on every nesting run
 *                    (every info not related to parts will be stale)
 * @param groups - contains information that doesn't
 *                 trigger nesting (like edge profiling, paint color and beams)
 **/
export const createBackendGrouping = (
  sheetGroups: SheetGroup[],
  groups: MaterialGroup[]
) => {
  const cutlistParts = partsSelector(useCutlistState.getState());
  const groupsState = useMaterialGroupState.getState();

  const groupsFromNesting = sheetGroups
    .map((sheetGroup, groupIndex) => {
      const {
        // can be stale when material-group-level data is updated but
        // nesting hasn't ran yet
        //
        // ex: update paint color but don't change any part data
        materialGroup: staleMaterialGroup,
        parts: _nestingParts, // eslint-disable-line @typescript-eslint/no-unused-vars
        smallPartEdgebandingLimits: _limits, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...nestingOutput
      } = sheetGroup;

      const materialGroup =
        groups.find((g) => g.id === staleMaterialGroup.id) ||
        staleMaterialGroup;

      // Use fresh store parts instead of the stale nesting snapshot so that
      // metadata changes (createLabel, edgeProfile, cncSeconds, etc.) are
      // always current at save time, even when nesting did not re-run.
      const groupParts = cutlistParts
        .filter((p) => p.groupId === materialGroup.id)
        .filter(isValidPart);
      if (!groupParts.length) return;
      const groupBackendParts = transformPartsToBackend(groupParts);

      return {
        parts: groupBackendParts,
        materialGroup: transformMaterialGroupToBackend(
          materialGroup,
          groupIndex,
          nestingOutput
        ),
      } as BackendCutlistGroup;
    })
    .filter(isTruthy);

  const beamGroups: BackendCutlistGroup[] = groupsState.groups
    .filter((g) => g.type === 'beams')
    .map(
      (g, index) =>
        ({
          materialGroup: transformMaterialGroupToBackend(
            g,
            groupsFromNesting.length + index,
            undefined
          ),

          parts: transformPartsToBackend(
            cutlistParts
              .filter((p) => p.groupId === g.id)
              .filter(isValidBeamPart)
              .sort(
                (a, b) =>
                  ((a.createdAt || a.index) ?? 0) -
                  ((b.createdAt || b.index) ?? 0)
              )
          ),
        }) as BackendCutlistGroup
    );

  return [...groupsFromNesting, ...beamGroups];
};

/**
 * Build backend grouping without any nesting output.
 *
 * Used by the flow where the frontend no longer runs nesting —
 * the backend nests each material group itself. Groups are derived directly
 * from the material groups, so it does not depend on `sheetGroups`.
 */
export const createBackendGroupingForNesting = (
  groups: MaterialGroup[]
): BackendCutlistGroup[] => {
  const cutlistParts = partsSelector(useCutlistState.getState());

  return groups
    .map((group, groupIndex) => {
      const isBeam = group.type === 'beams';
      const groupParts = cutlistParts
        .filter((p) => p.groupId === group.id)
        .filter(isBeam ? isValidBeamPart : isValidPart);
      if (!groupParts.length) return;

      return {
        parts: transformPartsToBackend(groupParts),
        materialGroup: transformMaterialGroupToBackend(
          group,
          groupIndex,
          undefined
        ),
      } as BackendCutlistGroup;
    })
    .filter(isTruthy);
};
